import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, CalendarRange, RefreshCw, Plug, ExternalLink, Video,
  Check, X, Clock3, MapPin, Users, Palmtree, CalendarOff, Home, Shield, Building2,
  CheckCircle2, Hourglass, Sparkles, AlertTriangle, Zap, Plane, PhoneCall,
  TrendingUp, Megaphone, Briefcase, Target, ArrowRight,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const DOW_LONG = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

// -------------------------------------------------------------------- utils
function pad(n) { return String(n).padStart(2, "0"); }
function isoOf(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function parseIso(iso) { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); }
function monthGridRange(cursor) {
  // Malla 6x7 empezando en lunes.
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first);
  const offset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - offset);
  const end = new Date(start);
  end.setDate(end.getDate() + 41);
  return { start, end };
}
function weekRange(cursor) {
  const start = new Date(cursor);
  const offset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - offset);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return { start, end };
}
function fmtDay(d) {
  return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}
function toDateOnly(iso) {
  if (!iso) return "";
  if (iso.length === 10) return iso;
  try { return isoOf(new Date(iso)); } catch { return iso.slice(0, 10); }
}
function hourOfIso(iso) {
  try { const d = new Date(iso); return d.getHours() + d.getMinutes() / 60; } catch { return 0; }
}
function daysBetween(a, b) { return Math.round((b - a) / 86400000); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

// Clasifica un item como "todo el día" o "con hora" para las vistas semanal/día.
function isTimedItem(it) {
  if (it.type === "gcal") return !it.allDay;
  if (it.type === "pipeline") return !!it.pipeline.when && it.pipeline.when.length > 10;
  if (it.type === "b2c_meeting") return true;
  if (it.type === "b2b_activity") return !!it.timed;
  return false;
}
// Devuelve el ISO de inicio para eventos timed (para posicionar en el timeline).
function timedStartIso(it) {
  if (it.type === "gcal") return it.ev.start;
  if (it.type === "pipeline") return it.pipeline.when;
  if (it.type === "b2c_meeting") return it.meeting.start_at;
  if (it.type === "b2b_activity") return it.activity.due_date;
  return null;
}
function timedEndIso(it) {
  if (it.type === "gcal") return it.ev.end;
  if (it.type === "b2c_meeting") {
    const s = new Date(it.meeting.start_at);
    return new Date(s.getTime() + (it.meeting.duration_min || 30) * 60000).toISOString();
  }
  // pipeline/b2b: 30 min por defecto
  const iso = timedStartIso(it);
  if (!iso) return null;
  const s = new Date(iso);
  return new Date(s.getTime() + 30 * 60000).toISOString();
}
function itemId(it) {
  if (it.type === "gcal") return it.ev.id;
  if (it.type === "pipeline") return `p-${it.pipeline.id}`;
  if (it.type === "trip") return `t-${it.trip.id}-${it.trip.kind}`;
  if (it.type === "b2c_meeting") return `m-${it.meeting.id}`;
  if (it.type === "b2b_activity") return `b-${it.activity.id}`;
  return it.id || Math.random().toString(36).slice(2);
}

// Reduce todas las cosas de un día en una estructura homogénea para pintar.
// Tipos: "gcal", "vacaciones", "ausencia", "guardia_libre", "remote", "guardia", "oficina_fija", "oficina_elegida", "festivo".
function buildDayMap(agenda) {
  const map = {}; // { "YYYY-MM-DD": [{type, ...}] }
  const push = (iso, item) => { if (!iso) return; (map[iso] ||= []).push(item); };
  if (!agenda) return map;
  const sofi = agenda.sofi || {};
  (sofi.holidays || []).forEach((h) => push(h.date, { type: "festivo", label: h.name, scope: h.scope }));
  (sofi.office_days || []).forEach((o) => push(o.date, {
    type: o.kind === "office_fixed" ? "oficina_fija" : "oficina_elegida",
    second_day: o.second_day, custom: !!o.custom,
  }));
  (sofi.remote_days || []).forEach((r) => push(r.date, {
    type: "remote", id: r.id, status: r.status, note: r.note,
    exceptional: !!r.exceptional,
  }));
  (sofi.absences || []).forEach((a) => {
    // Ausencia parcial (con hora): solo se pinta en el día concreto y como evento con hora.
    if (a.is_partial && a.time_start && a.time_end) {
      push(a.start, { type: "ausencia_partial", id: a.id, absence: a });
      return;
    }
    const start = parseIso(a.start), end = parseIso(a.end);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = isoOf(d);
      const isVac = a.kind === "vacaciones";
      const isLibre = a.kind === "guardia_libre";
      const t = isVac ? "vacaciones" : isLibre ? "guardia_libre" : "ausencia";
      push(iso, {
        type: t, id: a.id, status: a.status, reason: a.reason,
        approver_message: a.approver_message, source: a.source,
        start: a.start, end: a.end,
        justificante_required: a.justificante_required,
        justificante_uploaded: !!a.justificante_file_id,
      });
    }
  });
  (sofi.guardias || []).forEach((g) => {
    const start = parseIso(g.start), end = parseIso(g.end);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = isoOf(d);
      push(iso, { type: "guardia", id: g.id, role: g.role, status: g.status, start: g.start, end: g.end });
    }
  });
  (agenda.gcal_events || []).forEach((ev) => {
    if (ev.all_day) {
      const start = parseIso(ev.start);
      const endExcl = parseIso(ev.end || ev.start);
      const last = new Date(endExcl); last.setDate(last.getDate() - 1);
      const stop = last < start ? start : last;
      for (let d = new Date(start); d <= stop; d.setDate(d.getDate() + 1)) {
        push(isoOf(d), { type: "gcal", ev, allDay: true });
      }
    } else {
      push(toDateOnly(ev.start), { type: "gcal", ev, allDay: false });
    }
  });
  // Pipeline: recordatorios (opportunities + leads con next_action_at) — timed
  (agenda.pipeline || []).forEach((p) => {
    push(toDateOnly(p.when), { type: "pipeline", pipeline: p });
  });
  // Trips: salidas + regresos del agente — all-day
  (agenda.trips || []).forEach((t) => {
    push(t.date, { type: "trip", trip: t });
  });
  // B2C meetings — timed
  (agenda.b2c_meetings || []).forEach((m) => {
    push(toDateOnly(m.start_at), { type: "b2c_meeting", meeting: m });
  });
  // B2B activities — timed si viene con hora, si no all-day
  (agenda.b2b_activities || []).forEach((a) => {
    const iso = a.due_date || "";
    const dateOnly = iso.length === 10 ? iso : toDateOnly(iso);
    const timed = iso.length > 10;
    push(dateOnly, { type: "b2b_activity", activity: a, timed });
  });
  // Ordena cada día: primero all-day sofi, luego festivos, luego reuniones por hora.
  Object.values(map).forEach((arr) => {
    arr.sort((a, b) => {
      const timeOf = (x) => {
        if (x.type === "gcal" && !x.allDay) return hourOfIso(x.ev.start);
        if (x.type === "pipeline") return hourOfIso(x.pipeline.when);
        if (x.type === "b2c_meeting") return hourOfIso(x.meeting.start_at);
        if (x.type === "b2b_activity" && x.timed) return hourOfIso(x.activity.due_date);
        return -1; // all-day / sofi va primero
      };
      const ta = timeOf(a), tb = timeOf(b);
      if (ta < 0 && tb >= 0) return -1;
      if (tb < 0 && ta >= 0) return 1;
      return ta - tb;
    });
  });
  return map;
}

// Estilos y textos por tipo de item.
const CHIP = {
  gcal:            { cls: "border-indigo-300 bg-indigo-50 text-indigo-800",       icon: Video,        label: "Reunión" },
  gcal_pending:    { cls: "border-amber-300 bg-amber-50 text-amber-800",           icon: Video,        label: "Invitación" },
  vacaciones:      { cls: "border-sky-300 bg-sky-100 text-sky-800",                icon: Palmtree,     label: "Vacaciones" },
  vacaciones_pend: { cls: "border-dashed border-sky-400 bg-sky-50 text-sky-700",   icon: Hourglass,    label: "Vacaciones (solic.)" },
  ausencia:        { cls: "border-orange-300 bg-orange-100 text-orange-800",       icon: CalendarOff,  label: "Ausencia" },
  ausencia_pend:   { cls: "border-dashed border-orange-400 bg-orange-50 text-orange-700", icon: Hourglass, label: "Ausencia (solic.)" },
  guardia_libre:   { cls: "border-rose-300 bg-rose-100 text-rose-800",             icon: Zap,          label: "Libre por guardia" },
  remote:          { cls: "border-purple-300 bg-purple-100 text-purple-800",       icon: Home,         label: "Teletrabajo" },
  remote_exc:      { cls: "border-purple-400 bg-white text-purple-800",              icon: Home,         label: "Teletrabajo exc." },
  remote_pend:     { cls: "border-dashed border-purple-400 bg-purple-50 text-purple-700", icon: Hourglass, label: "Teletrabajo (solic.)" },
  ausencia_partial:{ cls: "border-orange-400 bg-orange-50 text-orange-900",         icon: Clock3,       label: "Ausencia por horas" },
  guardia:         { cls: "border-rose-300 bg-rose-50 text-rose-700",              icon: Shield,       label: "Guardia" },
  oficina_fija:    { cls: "border-blue-300 bg-blue-50 text-blue-800",              icon: Building2,    label: "Oficina · martes" },
  oficina_elegida: { cls: "border-emerald-300 bg-emerald-50 text-emerald-800",     icon: Building2,    label: "Oficina" },
  festivo:         { cls: "border-slate-300 bg-slate-100 text-slate-700",          icon: Sparkles,     label: "Festivo" },
  // Nuevos: Pipeline + Trips + B2C + B2B
  pipeline:        { cls: "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800",     icon: Target,       label: "Seguimiento" },
  trip_departure:  { cls: "border-teal-300 bg-teal-50 text-teal-800",              icon: Plane,        label: "Salida" },
  trip_return:     { cls: "border-teal-200 bg-white text-teal-700",                icon: Plane,        label: "Regreso" },
  b2c_meeting:     { cls: "border-amber-300 bg-amber-50 text-amber-900",           icon: Megaphone,    label: "Lead B2C" },
  b2b_activity:    { cls: "border-slate-400 bg-slate-100 text-slate-800",          icon: Briefcase,    label: "B2B" },
  b2b_done:        { cls: "border-emerald-200 bg-emerald-50 text-emerald-700 line-through opacity-70", icon: Briefcase, label: "B2B ✓" },
};

const B2B_TYPE_ICON = { call: PhoneCall, meeting: Video, email: Users, task: Briefcase, note: Sparkles };
const B2B_TYPE_LABEL = { call: "Llamada", meeting: "Reunión", email: "Email", task: "Tarea", note: "Nota" };

function chipOf(item) {
  if (item.type === "gcal") {
    return item.ev.pending_invite ? CHIP.gcal_pending : CHIP.gcal;
  }
  if (item.type === "vacaciones") return item.status === "pendiente" ? CHIP.vacaciones_pend : CHIP.vacaciones;
  if (item.type === "ausencia")   return item.status === "pendiente" ? CHIP.ausencia_pend   : CHIP.ausencia;
  if (item.type === "remote") {
    if (item.status === "pendiente") return CHIP.remote_pend;
    if (item.exceptional) return CHIP.remote_exc;
    return CHIP.remote;
  }
  if (item.type === "ausencia_partial") return CHIP.ausencia_partial;
  if (item.type === "trip")       return item.trip.kind === "return" ? CHIP.trip_return : CHIP.trip_departure;
  if (item.type === "b2b_activity") {
    if (item.activity.done) return CHIP.b2b_done;
    const base = CHIP.b2b_activity;
    return { ...base, icon: B2B_TYPE_ICON[item.activity.type] || Briefcase };
  }
  return CHIP[item.type] || { cls: "border-slate-200 bg-slate-50 text-slate-700", icon: Sparkles, label: item.type };
}

function labelForItem(item) {
  if (item.type === "gcal") return item.allDay ? item.ev.summary : `${fmtTime(item.ev.start)} · ${item.ev.summary}`;
  if (item.type === "ausencia_partial") {
    const a = item.absence;
    return `${a.time_start}–${a.time_end} · ${a.reason || "Ausencia"}`;
  }
  if (item.type === "remote" && item.exceptional) return "Teletrabajo excepcional";
  if (item.type === "pipeline") {
    const p = item.pipeline;
    const t = p.when ? fmtTime(p.when) : "";
    const tag = p.source === "opportunity" ? "Op" : "Lead";
    return `${t ? t + " · " : ""}${tag}: ${p.title}`;
  }
  if (item.type === "trip") {
    const t = item.trip;
    const arrow = t.kind === "return" ? "↩" : "✈";
    return `${arrow} ${t.traveler}${t.destination ? ` — ${t.destination}` : ""}`;
  }
  if (item.type === "b2c_meeting") {
    const m = item.meeting;
    return `${fmtTime(m.start_at)} · ${m.lead_name || "Lead"} (${m.title})`;
  }
  if (item.type === "b2b_activity") {
    const a = item.activity;
    const t = item.timed ? fmtTime(a.due_date) + " · " : "";
    return `${t}${B2B_TYPE_LABEL[a.type] || "Tarea"}: ${a.subject}`;
  }
  return chipOf(item).label;
}

// ------------------------------------------------------------ Componentes UI
function ConnectionBanner({ agenda, onSync, syncing }) {
  if (!agenda) return null;
  if (!agenda.gcal_connected) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="agenda-gcal-banner-disconnected">
        <p className="flex items-center gap-2 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          Tu Google Calendar no está conectado. Solo se muestran los eventos de SOFI.
        </p>
        <p className="text-xs text-amber-800">Conecta tu Google desde el icono <CalendarRange className="mb-0.5 inline h-3.5 w-3.5" /> del header.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/30 p-3" data-testid="agenda-gcal-banner-connected">
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        Conectado a <strong className="text-foreground">{agenda.gcal_email}</strong> · las reuniones de Google y los eventos del Portal se muestran juntos.
      </p>
      <Button size="sm" variant="outline" className="h-7" onClick={onSync} disabled={syncing} data-testid="agenda-sync-portal">
        <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
        Empujar SOFI a Google
      </Button>
    </div>
  );
}

function Legend() {
  const items = [
    ["gcal", "Reunión"], ["gcal_pending", "Invitación"],
    ["pipeline", "Pipeline"], ["b2c_meeting", "Lead B2C"], ["b2b_activity", "B2B"],
    ["trip_departure", "Salida viaje"], ["trip_return", "Regreso viaje"],
    ["oficina_fija", "Oficina · martes"], ["oficina_elegida", "Oficina · elegido"],
    ["remote", "Teletrabajo"], ["vacaciones", "Vacaciones"], ["ausencia", "Ausencia"],
    ["guardia_libre", "Libre por guardia"], ["guardia", "Guardia"], ["festivo", "Festivo"],
  ];
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground" data-testid="agenda-legend">
      {items.map(([k, label]) => (
        <span key={k} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 ${CHIP[k]?.cls || ""}`}>
          <span className="h-2 w-2 rounded-sm" /> {label}
        </span>
      ))}
    </div>
  );
}

// ---- Detalle de evento (dialog)
function EventDetailPopover({ open, onClose, item, onRespond, onNavigate }) {
  if (!open) return null;
  const isG = item.type === "gcal";
  const isP = item.type === "pipeline";
  const isT = item.type === "trip";
  const isB2C = item.type === "b2c_meeting";
  const isB2B = item.type === "b2b_activity";
  const chip = chipOf(item);
  const TitleIcon = chip.icon;
  const titleText = isG ? item.ev.summary
    : isP ? (item.pipeline.source === "opportunity" ? "Oportunidad" : "Lead") + ": " + item.pipeline.title
    : isT ? (item.trip.kind === "return" ? "Regreso" : "Salida") + " · " + item.trip.traveler
    : isB2C ? "Reunión con lead B2C"
    : isB2B ? (B2B_TYPE_LABEL[item.activity.type] || "Tarea") + ": " + item.activity.subject
    : chip.label;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="agenda-event-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <TitleIcon className="h-4 w-4" />{titleText}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {isG && (
            <>
              <p className="flex items-center gap-1.5 text-muted-foreground">
                <Clock3 className="h-3.5 w-3.5" />
                {item.ev.all_day ? "Todo el día" : `${fmtTime(item.ev.start)} – ${fmtTime(item.ev.end)}`}
              </p>
              {item.ev.location && <p className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{item.ev.location}</p>}
              {item.ev.attendees_count > 0 && <p className="flex items-center gap-1.5 text-muted-foreground"><Users className="h-3.5 w-3.5" />{item.ev.attendees_count} asistente(s)</p>}
              {item.ev.description && <p className="whitespace-pre-wrap rounded-md bg-muted p-2 text-xs">{item.ev.description}</p>}
              {item.ev.pending_invite && (
                <div className="flex flex-wrap gap-1.5 pt-2">
                  <Button size="sm" onClick={() => onRespond(item.ev.id, "accepted")} data-testid={`agenda-accept-${item.ev.id}`}><Check className="mr-1 h-3.5 w-3.5" />Aceptar</Button>
                  <Button size="sm" variant="outline" onClick={() => onRespond(item.ev.id, "tentative")} data-testid={`agenda-tentative-${item.ev.id}`}>Quizás</Button>
                  <Button size="sm" variant="outline" className="text-rose-600" onClick={() => onRespond(item.ev.id, "declined")} data-testid={`agenda-decline-${item.ev.id}`}><X className="mr-1 h-3.5 w-3.5" />Rechazar</Button>
                </div>
              )}
              {!item.ev.pending_invite && item.ev.self_status && item.ev.self_status !== "accepted" && (
                <p className="text-[11px] text-muted-foreground">Tu respuesta: {item.ev.self_status === "declined" ? "rechazada" : item.ev.self_status === "tentative" ? "quizás" : item.ev.self_status}</p>
              )}
            </>
          )}
          {isP && (
            <>
              <p className="flex items-center gap-1.5 text-muted-foreground">
                <Clock3 className="h-3.5 w-3.5" />
                {item.pipeline.when ? new Date(item.pipeline.when).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : "Sin hora"}
              </p>
              {item.pipeline.destination && <p className="text-xs text-muted-foreground">Destino: {item.pipeline.destination}</p>}
              {item.pipeline.note && <p className="whitespace-pre-wrap rounded-md bg-muted p-2 text-xs">{item.pipeline.note}</p>}
              <p className="text-[11px] text-muted-foreground">Este recordatorio se sincronizó a tu Google Calendar cuando lo agendaste.</p>
            </>
          )}
          {isT && (
            <>
              <p className="text-xs text-muted-foreground">{item.trip.destination || "Sin destino"}</p>
              <p className="text-xs">Estado: <strong>{item.trip.status || "—"}</strong>{item.trip.source ? ` · Fuente: ${item.trip.source}` : ""}</p>
              <p className="text-[11px] text-muted-foreground">{item.trip.kind === "return" ? "Fecha de regreso" : "Fecha de salida"}: {item.date}</p>
            </>
          )}
          {isB2C && (
            <>
              <p className="flex items-center gap-1.5 text-muted-foreground">
                <Clock3 className="h-3.5 w-3.5" />
                {new Date(item.meeting.start_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })} · {item.meeting.duration_min} min
              </p>
              <p className="text-xs"><strong>{item.meeting.lead_name}</strong> · <span className="text-muted-foreground">{item.meeting.lead_email}</span></p>
              {item.meeting.notes && <p className="whitespace-pre-wrap rounded-md bg-muted p-2 text-xs">{item.meeting.notes}</p>}
              <p className="text-[11px] text-muted-foreground">{item.meeting.gcal_synced ? "✓ Sincronizada con Google Calendar" : "Solo interna (Google no conectado en B2C)"}</p>
            </>
          )}
          {isB2B && (
            <>
              {item.activity.due_date && (
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock3 className="h-3.5 w-3.5" />
                  {item.timed
                    ? new Date(item.activity.due_date).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })
                    : item.activity.due_date}
                </p>
              )}
              <p className="text-xs">Tipo: <strong>{B2B_TYPE_LABEL[item.activity.type] || item.activity.type}</strong>{item.activity.done ? " · ✓ Hecho" : ""}</p>
              {item.activity.description && <p className="whitespace-pre-wrap rounded-md bg-muted p-2 text-xs">{item.activity.description}</p>}
            </>
          )}
          {!isG && !isP && !isT && !isB2C && !isB2B && (
            <>
              {item.status && <p className="text-xs text-muted-foreground">Estado: <strong>{item.status}</strong></p>}
              {item.reason && <p className="text-xs">Motivo: {item.reason}</p>}
              {item.note && <p className="text-xs">Nota: {item.note}</p>}
              {item.role && <p className="text-xs">Rol: {item.role}</p>}
              {item.start && item.end && item.start !== item.end && <p className="text-xs text-muted-foreground">{item.start} → {item.end}</p>}
              {item.approver_message && <p className="rounded-md bg-muted p-2 text-xs italic">💬 {item.approver_message}</p>}
            </>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {isG && item.ev.meet_link && (
            <a href={item.ev.meet_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="agenda-meet-link">
              <Video className="h-4 w-4" />Unirse a Google Meet
            </a>
          )}
          {isG && item.ev.html_link && (
            <a href={item.ev.html_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="agenda-gcal-link">
              <ExternalLink className="h-3 w-3" />Abrir en Google
            </a>
          )}
          {(isP || isT || isB2C || isB2B) && (() => {
            const link = isP ? item.pipeline.link
              : isT ? item.trip.link
              : isB2C ? item.meeting.link
              : item.activity.link;
            const label = isP ? "Abrir en Pipeline"
              : isT ? "Ver viaje"
              : isB2C ? "Abrir en B2C Marketing"
              : "Abrir en CRM B2B";
            return (
              <Button size="sm" variant="outline" onClick={() => { onNavigate(link); onClose(); }} data-testid="agenda-open-source">
                <ArrowRight className="mr-1 h-3.5 w-3.5" />{label}
              </Button>
            );
          })()}
          {isP && item.pipeline.gcal_link && (
            <a href={item.pipeline.gcal_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              <ExternalLink className="h-3 w-3" />Abrir en Google
            </a>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Chip pequeño dentro de una celda mensual
function CellChip({ item, onClick }) {
  const c = chipOf(item);
  const Icon = c.icon;
  const label = labelForItem(item);
  return (
    <button onClick={onClick} title={label}
      className={`flex w-full items-center gap-1 truncate rounded-md border px-1.5 py-0.5 text-left text-[10.5px] font-medium ${c.cls} transition-colors hover:brightness-95`}
      data-testid={`agenda-chip-${item.type}`}>
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}

// ---- Vista MES
function MonthView({ cursor, dayMap, todayIso, onOpen }) {
  const { start } = monthGridRange(cursor);
  const cells = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(start, i));
  const curMonth = cursor.getMonth();
  return (
    <div className="overflow-hidden rounded-lg border bg-card" data-testid="agenda-month">
      <div className="grid grid-cols-7 border-b bg-muted/40 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {DOW.map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const iso = isoOf(d);
          const inMonth = d.getMonth() === curMonth;
          const isToday = iso === todayIso;
          const items = dayMap[iso] || [];
          const visible = items.slice(0, 4);
          const rest = items.length - visible.length;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <div key={i} className={`min-h-[110px] border-b border-r p-1.5 last:border-r ${(i + 1) % 7 === 0 ? "border-r-0" : ""} ${!inMonth ? "bg-muted/30" : ""} ${isWeekend && inMonth ? "bg-muted/10" : ""}`}
                data-testid={`agenda-day-${iso}`}>
              <div className="mb-1 flex items-center justify-between">
                <span className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold ${isToday ? "bg-primary text-primary-foreground" : inMonth ? "text-foreground" : "text-muted-foreground"}`}>
                  {d.getDate()}
                </span>
              </div>
              <div className="space-y-1">
                {visible.map((it, ix) => <CellChip key={ix} item={it} onClick={() => onOpen(it)} />)}
                {rest > 0 && (
                  <button className="text-[10px] font-medium text-muted-foreground hover:text-foreground" onClick={() => onOpen({ __more: true, iso, items })} data-testid={`agenda-more-${iso}`}>
                    + {rest} más
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Vista SEMANA (timeline horario 7–21)
const START_HOUR = 7, END_HOUR = 22;
const HOUR_PX = 40;
function WeekView({ cursor, dayMap, todayIso, onOpen }) {
  const { start } = weekRange(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="overflow-hidden rounded-lg border bg-card" data-testid="agenda-week">
      <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b bg-muted/40 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <div />
        {days.map((d, i) => {
          const iso = isoOf(d);
          const isToday = iso === todayIso;
          return (
            <div key={i} className={`px-2 py-2 ${isToday ? "text-primary" : ""}`}>
              <div>{DOW[i]}</div>
              <div className={`mt-0.5 inline-flex h-6 min-w-6 items-center justify-center rounded-full text-sm ${isToday ? "bg-primary text-primary-foreground" : "text-foreground"}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      {/* Fila all-day */}
      <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b bg-muted/10">
        <div className="p-1.5 text-right text-[10px] uppercase tracking-wider text-muted-foreground">todo el día</div>
        {days.map((d, i) => {
          const iso = isoOf(d);
          const items = (dayMap[iso] || []).filter((it) => !isTimedItem(it));
          return (
            <div key={i} className="min-h-[36px] space-y-1 border-l p-1" data-testid={`agenda-allday-${iso}`}>
              {items.map((it, ix) => <CellChip key={ix} item={it} onClick={() => onOpen(it)} />)}
            </div>
          );
        })}
      </div>
      {/* Cuadrícula horaria */}
      <div className="grid grid-cols-[56px_repeat(7,1fr)]">
        <div>
          {Array.from({ length: END_HOUR - START_HOUR }, (_, k) => (
            <div key={k} style={{ height: HOUR_PX }} className="pr-1 text-right text-[10px] leading-none text-muted-foreground">
              <span className="relative -top-1.5">{String(START_HOUR + k).padStart(2, "0")}:00</span>
            </div>
          ))}
        </div>
        {days.map((d, i) => {
          const iso = isoOf(d);
          const items = (dayMap[iso] || []).filter(isTimedItem);
          const isToday = iso === todayIso;
          return (
            <div key={i} className={`relative border-l ${isToday ? "bg-primary/[0.03]" : ""}`} style={{ height: (END_HOUR - START_HOUR) * HOUR_PX }} data-testid={`agenda-col-${iso}`}>
              {/* Rejilla */}
              {Array.from({ length: END_HOUR - START_HOUR }, (_, k) => (
                <div key={k} style={{ top: k * HOUR_PX }} className="absolute inset-x-0 border-t border-dashed border-border/40" />
              ))}
              {/* Eventos */}
              {items.map((it, ix) => {
                const sIso = timedStartIso(it);
                const eIso = timedEndIso(it);
                if (!sIso) return null;
                const startH = Math.max(hourOfIso(sIso), START_HOUR);
                const endH = Math.min(hourOfIso(eIso) || (startH + 0.5), END_HOUR);
                if (endH <= START_HOUR || startH >= END_HOUR) return null;
                const top = (startH - START_HOUR) * HOUR_PX;
                const height = Math.max((endH - startH) * HOUR_PX, 22);
                const c = chipOf(it);
                const Icon = c.icon;
                const title = it.type === "gcal" ? it.ev.summary
                  : it.type === "pipeline" ? it.pipeline.title
                  : it.type === "b2c_meeting" ? (it.meeting.lead_name || it.meeting.title)
                  : it.type === "b2b_activity" ? it.activity.subject
                  : chipOf(it).label;
                return (
                  <button key={ix} onClick={() => onOpen(it)}
                    style={{ top, height }}
                    className={`absolute inset-x-1 overflow-hidden rounded-md border px-1.5 py-1 text-left text-[10.5px] shadow-sm ${c.cls} hover:brightness-95`}
                    data-testid={`agenda-w-ev-${itemId(it)}`}>
                    <div className="flex items-center gap-1 font-semibold"><Icon className="h-3 w-3" />{fmtTime(sIso)}</div>
                    <div className="truncate">{title}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Vista DÍA (lista cronológica)
function DayView({ cursor, dayMap, todayIso, onOpen }) {
  const iso = isoOf(cursor);
  const items = dayMap[iso] || [];
  const allDay = items.filter((it) => !isTimedItem(it));
  const timed  = items.filter(isTimedItem);
  const isToday = iso === todayIso;
  return (
    <div className="space-y-3" data-testid="agenda-day">
      <div className="flex items-center gap-2">
        <h3 className={`font-heading text-lg font-semibold capitalize ${isToday ? "text-primary" : ""}`}>{fmtDay(cursor)}</h3>
        {isToday && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">Hoy</span>}
      </div>
      {allDay.length > 0 && (
        <Card><CardContent className="space-y-1.5 p-3">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Todo el día</p>
          {allDay.map((it, ix) => <CellChip key={ix} item={it} onClick={() => onOpen(it)} />)}
        </CardContent></Card>
      )}
      <Card><CardContent className="p-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Reuniones y seguimientos</p>
        {timed.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin reuniones ni seguimientos programados.</p>
        ) : (
          <ul className="space-y-2">
            {timed.map((it, ix) => {
              const c = chipOf(it);
              const Icon = c.icon;
              const sIso = timedStartIso(it);
              const eIso = timedEndIso(it);
              const title = it.type === "gcal" ? it.ev.summary
                : it.type === "pipeline" ? (it.pipeline.source === "opportunity" ? "Op: " : "Lead: ") + it.pipeline.title
                : it.type === "b2c_meeting" ? `${it.meeting.lead_name} — ${it.meeting.title}`
                : it.type === "b2b_activity" ? `${B2B_TYPE_LABEL[it.activity.type] || "Tarea"}: ${it.activity.subject}`
                : chipOf(it).label;
              const subtitle = it.type === "gcal" ? it.ev.location
                : it.type === "b2c_meeting" ? it.meeting.lead_email
                : it.type === "pipeline" ? it.pipeline.note
                : it.type === "b2b_activity" ? it.activity.description
                : "";
              return (
                <li key={ix} className={`flex items-center gap-3 rounded-md border p-2.5 ${c.cls}`} data-testid={`agenda-d-ev-${itemId(it)}`}>
                  <div className="text-xs font-semibold whitespace-nowrap">{fmtTime(sIso)}{eIso ? <><br/>{fmtTime(eIso)}</> : null}</div>
                  <div className="min-w-0 flex-1">
                    <button onClick={() => onOpen(it)} className="block truncate text-left text-sm font-semibold hover:underline"><Icon className="mr-1 inline h-3.5 w-3.5" />{title}</button>
                    {subtitle && <p className="truncate text-[11px] text-muted-foreground">{subtitle}</p>}
                  </div>
                  {it.type === "gcal" && it.ev.meet_link && <a href={it.ev.meet_link} target="_blank" rel="noreferrer" title="Unirse Meet" className="text-primary"><Video className="h-4 w-4" /></a>}
                  {it.type === "gcal" && it.ev.html_link && <a href={it.ev.html_link} target="_blank" rel="noreferrer" title="Abrir Google" className="text-muted-foreground"><ExternalLink className="h-4 w-4" /></a>}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent></Card>
    </div>
  );
}

// ---- Diálogo "más eventos"
function MoreDialog({ open, onClose, payload, onOpenItem }) {
  if (!open || !payload) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="agenda-more-dialog">
        <DialogHeader>
          <DialogTitle className="capitalize">{fmtDay(parseIso(payload.iso))}</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          {payload.items.map((it, ix) => <CellChip key={ix} item={it} onClick={() => { onClose(); onOpenItem(it); }} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==========================================================================
export default function MiAgendaPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [view, setView] = useState("day"); // month | week | day
  const [cursor, setCursor] = useState(() => new Date());
  const [detail, setDetail] = useState(null);
  const [more, setMore] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const goToSource = (link) => {
    if (!link) return;
    // Enlaces externos absolutos (http/https)
    if (/^https?:\/\//i.test(link)) { window.open(link, "_blank", "noopener,noreferrer"); return; }
    navigate(link);
  };

  // Rango a pedir: depende de la vista.
  const range = useMemo(() => {
    if (view === "month") {
      const r = monthGridRange(cursor);
      return { start: isoOf(r.start), end: isoOf(r.end) };
    }
    if (view === "week") {
      const r = weekRange(cursor);
      return { start: isoOf(r.start), end: isoOf(r.end) };
    }
    return { start: isoOf(cursor), end: isoOf(cursor) };
  }, [view, cursor]);

  const { data: agenda, isLoading, refetch } = useQuery({
    queryKey: ["agenda-mine", range.start, range.end],
    queryFn: async () => (await api.get("/agenda/mine", { params: range })).data,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
  const dayMap = useMemo(() => buildDayMap(agenda), [agenda]);
  const todayIso = agenda?.today || isoOf(new Date());

  const move = (n) => {
    setCursor((c) => {
      const nc = new Date(c);
      if (view === "month") nc.setMonth(nc.getMonth() + n);
      else if (view === "week") nc.setDate(nc.getDate() + 7 * n);
      else nc.setDate(nc.getDate() + n);
      return nc;
    });
  };
  const goToday = () => setCursor(new Date());

  const respond = async (eventId, response) => {
    try {
      await api.post(`/gcal/events/${eventId}/respond`, { response });
      toast.success(response === "accepted" ? "Invitación aceptada" : response === "declined" ? "Invitación rechazada" : "Marcada como quizás");
      setDetail(null);
      qc.invalidateQueries({ queryKey: ["agenda-mine"] });
      qc.invalidateQueries({ queryKey: ["gcal-upcoming"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo responder"); }
  };

  const syncPortal = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/gcal/sync-portal");
      const parts = [];
      if (data.created) parts.push(`${data.created} nuevos`);
      if (data.updated) parts.push(`${data.updated} actualizados`);
      if (data.deleted) parts.push(`${data.deleted} eliminados`);
      toast.success("Agenda de SOFI sincronizada con Google", { description: parts.length ? parts.join(" · ") : "Todo al día" });
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo sincronizar"); }
    finally { setSyncing(false); }
  };

  const headerLabel = view === "month"
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === "week"
      ? (() => { const w = weekRange(cursor); return `Semana ${w.start.getDate()} ${MONTHS[w.start.getMonth()].slice(0,3)} – ${w.end.getDate()} ${MONTHS[w.end.getMonth()].slice(0,3)}`; })()
      : fmtDay(cursor);

  return (
    <div className="space-y-4" data-testid="mi-agenda-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <CalendarRange className="h-6 w-6 text-primary" /> Mi Agenda
          </h1>
          <p className="text-sm text-muted-foreground">
            Google Calendar, Pipeline, Viajes, B2C, B2B, ausencias, teletrabajo, oficina y guardias — todo unificado.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-lg border">
            {["month", "week", "day"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`agenda-view-${v}`}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === v ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}>
                {v === "month" ? "Mes" : v === "week" ? "Semana" : "Día"}
              </button>
            ))}
          </div>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(-1)} data-testid="agenda-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={goToday} data-testid="agenda-today">Hoy</Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(1)} data-testid="agenda-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <ConnectionBanner agenda={agenda} onSync={syncPortal} syncing={syncing} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg font-semibold capitalize" data-testid="agenda-title">{headerLabel}</h2>
        <Legend />
      </div>

      {isLoading ? (
        <Skeleton className="h-[560px] w-full" />
      ) : view === "month" ? (
        <MonthView cursor={cursor} dayMap={dayMap} todayIso={todayIso} onOpen={(it) => it.__more ? setMore(it) : setDetail(it)} />
      ) : view === "week" ? (
        <WeekView cursor={cursor} dayMap={dayMap} todayIso={todayIso} onOpen={setDetail} />
      ) : (
        <DayView cursor={cursor} dayMap={dayMap} todayIso={todayIso} onOpen={setDetail} />
      )}

      <EventDetailPopover open={!!detail && !detail.__more} item={detail || {}} onClose={() => setDetail(null)} onRespond={respond} onNavigate={goToSource} />
      <MoreDialog open={!!more} payload={more} onClose={() => setMore(null)} onOpenItem={setDetail} />
    </div>
  );
}
